import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { contentApi } from '../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Filter = 'all' | 'missing' | 'covered'

export function useRecommendationCoverage() {
  return useQuery({
    queryKey: ['rec-coverage'],
    queryFn: () => contentApi.getRecommendationCoverage(),
    refetchInterval: 30000,
  })
}

export function useRecommendationProducts(filter: Filter) {
  return useInfiniteQuery({
    queryKey: ['rec-products', filter],
    queryFn: ({ pageParam }) => contentApi.getRecommendationProducts({ filter, cursor: (pageParam as string | null) ?? null }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.pageInfo.hasNextPage ? last.pageInfo.cursor : undefined),
  })
}

export function useGenerateRecommendation() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ handle, title }: { handle: string; title?: string }) => contentApi.generateRecommendation(handle, title),
    onSuccess: (r) => {
      toast({ title: r.lanes ? `Generated ${r.lanes} lanes for ${r.handle}` : `No lanes for ${r.handle}` })
      qc.invalidateQueries({ queryKey: ['rec-products'] })
      qc.invalidateQueries({ queryKey: ['rec-coverage'] })
    },
    onError: (e: any) => toast({ title: 'Generate failed', description: e?.response?.data?.error || e.message, variant: 'destructive' }),
  })
}

export function useEnqueueRecommendations() {
  const { toast } = useToast()
  return useMutation({
    mutationFn: (handles: string[]) => contentApi.enqueueRecommendations(handles),
    onSuccess: (r) => toast({ title: `Enqueued ${r.enqueued}${r.failed ? ` · ${r.failed} failed` : ''}`, description: 'Tasks will process in the background.' }),
    onError: (e: any) => toast({ title: 'Enqueue failed', description: e?.response?.data?.error || e.message, variant: 'destructive' }),
  })
}
